/*
 * Click-the-clock picker modal. Three tabs along the top:
 *   - Countdown   start a countdown that fires a takeover at zero
 *   - Timer       counts up; the teacher stops it manually
 *   - Alarms      list of recurring time-of-day alarms
 *
 * Lives inside the existing <Modal/> shell so it inherits the glass
 * card + ESC-to-close + backdrop-click behavior.
 */

.body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: var(--token-font-body);
}

.tabs {
  display: flex;
  gap: 4px;
  background: rgba(28, 38, 32, 0.06);
  padding: 4px;
  border-radius: 999px;
  align-self: flex-start;
}

.tab {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
  padding: 6px 16px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  color: rgba(28, 38, 32, 0.7);
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease;
}

.tab:hover:not([aria-pressed='true']) {
  background: rgba(255, 255, 255, 0.7);
}

.tab[aria-pressed='true'] {
  background: var(--token-accent);
  color: #fff;
}

/* ---------- Active panel (shown when running countdown/timer) ---------- */

.activeCard {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: color-mix(in srgb, var(--token-accent) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--token-accent) 40%, transparent);
}

.activeMeta {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.activeKind {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(140, 78, 30, 0.95);
}

.activeName {
  font-family: var(--token-font-display);
  font-size: 20px;
  color: var(--token-text-primary);
}

.activeTime {
  font-family: var(--token-font-display);
  font-size: 32px;
  font-weight: 700;
  color: var(--token-accent);
  font-variant-numeric: tabular-nums;
  letter-spacing: 1px;
}

.stopButton {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1.5px solid rgba(176, 58, 46, 0.5);
  background: transparent;
  color: rgba(176, 58, 46, 0.95);
  cursor: pointer;
  transition: background 120ms ease;
}

.stopButton:hover {
  background: rgba(176, 58, 46, 0.12);
}

/* ---------- Form fields ---------- */

.formRow {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.formGroup {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.fieldLabel {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.6);
}

.input {
  font-family: var(--token-font-body);
  font-size: 15px;
  color: var(--token-text-primary);
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.85);
  width: 100%;
  box-sizing: border-box;
  font-variant-numeric: tabular-nums;
}

.input:focus {
  outline: none;
  border-color: var(--token-accent);
  background: #fff;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--token-accent) 18%, transparent);
}

.presets {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.preset {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1.5px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.6);
  color: var(--token-text-primary);
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    color 120ms ease;
}

.preset:hover:not([aria-pressed='true']) {
  background: #fff;
  border-color: rgba(28, 38, 32, 0.32);
}

.preset[aria-pressed='true'] {
  background: var(--token-accent);
  border-color: var(--token-accent);
  color: #fff;
}

.startButton {
  font-family: var(--token-font-ui);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 11px 22px;
  border-radius: 12px;
  border: 1.5px solid var(--token-accent);
  background: var(--token-accent);
  color: #fff;
  cursor: pointer;
  align-self: flex-end;
  transition:
    background 120ms ease,
    border-color 120ms ease;
}

.startButton:hover:not(:disabled) {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
  border-color: color-mix(in srgb, var(--token-accent) 85%, #000);
}

.startButton:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.hint {
  margin: 0;
  font-size: 12px;
  color: rgba(28, 38, 32, 0.6);
  line-height: 1.4;
}

/* ---------- Alarms list ---------- */

.alarmList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 240px;
  overflow-y: auto;
}

.alarmItem {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(28, 38, 32, 0.1);
}

.alarmTime {
  font-family: var(--token-font-display);
  font-size: 22px;
  font-weight: 700;
  color: var(--token-accent);
  min-width: 96px;
  font-variant-numeric: tabular-nums;
}

.alarmName {
  flex: 1;
  font-size: 15px;
  color: var(--token-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alarmRemove {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(28, 38, 32, 0.16);
  background: transparent;
  color: rgba(176, 58, 46, 0.85);
  font-size: 14px;
  cursor: pointer;
}

.alarmRemove:hover {
  background: rgba(176, 58, 46, 0.08);
  border-color: rgba(176, 58, 46, 0.4);
}

.empty {
  margin: 0;
  font-size: 13px;
  color: rgba(28, 38, 32, 0.5);
  font-style: italic;
}
